import React, { useState } from 'react';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { CreateRequeteDialog } from './CreateRequeteDialog';
import { SearchIcon, FilterIcon, MapPinIcon, MapIcon, TableIcon, FolderIcon, ChevronLeftIcon, ChevronRightIcon, ChevronsUpDownIcon, PlusIcon, SettingsIcon, XIcon, PaperclipIcon, CheckSquareIcon, SquareIcon, LinkIcon, TagIcon, FileTextIcon, PrinterIcon } from 'lucide-react';
type ViewMode = 'list' | 'map';
type Status = 'Nouvelle demande' | 'En Cours' | 'Complété' | 'En Attente' | 'Reçue';
interface Request {
  id: string;
  subject: string;
  category: string;
  reqId: string;
  location: string;
  date: string;
  status: Status;
  department: string;
  requester: {
    name: string;
    initials: string;
    color: string;
  };
  slaDate: string;
}
const MOCK_REQUESTS: Request[] = [{
  id: '1',
  subject: 'Divers',
  category: 'Voirie et infrastructures routi...',
  reqId: 'R-101',
  location: '1520, 1 ère Rue, Richelieu,...',
  date: '8 juin 2026',
  status: 'Nouvelle demande',
  department: 'Service Des Travaux Publics',
  requester: {
    name: 'Nathalie Ouellet',
    initials: 'NO',
    color: 'bg-[#4F46E5]'
  },
  slaDate: '13 juin 2026'
}, {
  id: '2',
  subject: 'Bâtiment',
  category: 'Bâtiment',
  reqId: 'R-100',
  location: '205, Avenue 8e, Richelie...',
  date: '8 juin 2026',
  status: 'Nouvelle demande',
  department: 'Service Des Travaux Publics',
  requester: {
    name: 'Nathalie Ouellet',
    initials: 'NO',
    color: 'bg-[#4F46E5]'
  },
  slaDate: '8 juillet 2026'
}, {
  id: '3',
  subject: 'Nid-de-poule - trait...',
  category: 'Voirie et infrastructures routi...',
  reqId: 'R-99',
  location: '2907, Rang de la Savane, ...',
  date: '8 juin 2026',
  status: 'Nouvelle demande',
  department: 'Service Des Travaux Publics',
  requester: {
    name: 'Caroline Nadeau',
    initials: 'CN',
    color: 'bg-[#4F46E5]'
  },
  slaDate: '13 juin 2026'
}, {
  id: '4',
  subject: 'Divers',
  category: 'Voirie et infrastructures routi...',
  reqId: 'R-98',
  location: '32, Place Rouville-Tessier,...',
  date: '8 juin 2026',
  status: 'Complété',
  department: 'Service Des Travaux Publics',
  requester: {
    name: 'Nathalie Ouellet',
    initials: 'NO',
    color: 'bg-[#4F46E5]'
  },
  slaDate: '13 juin 2026'
}, {
  id: '5',
  subject: 'Divers',
  category: 'Voirie et infrastructures routi...',
  reqId: 'R-97',
  location: '700, Rue Martel, Richelie...',
  date: '4 juin 2026',
  status: 'En Cours',
  department: 'Service Des Travaux Publics',
  requester: {
    name: 'Nathalie Ouellet',
    initials: 'NO',
    color: 'bg-[#4F46E5]'
  },
  slaDate: '9 juin 2026'
}, {
  id: '6',
  subject: 'Plateaux sportifs - t...',
  category: 'Parcs et espaces récréatifs',
  reqId: 'R-96',
  location: '900, Rue des Oblats, Ric...',
  date: '3 juin 2026',
  status: 'Complété',
  department: 'Service Des Travaux Publics',
  requester: {
    name: 'Nathalie Ouellet',
    initials: 'NO',
    color: 'bg-[#4F46E5]'
  },
  slaDate: '8 juin 2026'
}, {
  id: '7',
  subject: 'Refoulement égout...',
  category: 'Eau potable et égout',
  reqId: 'R-95',
  location: '192, Rue des Érables, Ric...',
  date: '3 juin 2026',
  status: 'Nouvelle demande',
  department: 'Service Des Travaux Publics',
  requester: {
    name: 'Éric Robin',
    initials: 'ÉR',
    color: 'bg-[#4F46E5]'
  },
  slaDate: '4 juin 2026'
}];
const getStatusColor = (status: Status) => {
  switch (status) {
    case 'Nouvelle demande':
      return 'bg-slate-200 text-slate-700';
    case 'En Cours':
      return 'bg-indigo-100 text-indigo-700';
    case 'Complété':
      return 'bg-indigo-100 text-indigo-700';
    case 'En Attente':
      return 'bg-orange-100 text-orange-700';
    case 'Reçue':
      return 'bg-slate-100 text-slate-600';
    default:
      return 'bg-slate-100 text-slate-700';
  }
};
const getStatusDot = (status: Status) => {
  switch (status) {
    case 'Nouvelle demande':
      return 'bg-slate-400';
    case 'En Cours':
      return 'bg-[#4F46E5]';
    case 'Complété':
      return 'bg-[#4F46E5]';
    case 'En Attente':
      return 'bg-orange-500';
    case 'Reçue':
      return 'bg-slate-400';
    default:
      return 'bg-slate-400';
  }
};
// Request locations around Windsor, QC
const MAP_MARKERS: {
  reqId: string;
  subject: string;
  category: string;
  position: [number, number];
}[] = [{
  reqId: 'R-127',
  subject: 'Taxation',
  category: 'Administration',
  position: [45.4528, -73.4622]
}, {
  reqId: 'R-104',
  subject: 'Lampadaire défectueux',
  category: 'Éclairage public',
  position: [45.4468, -73.4698]
}, {
  reqId: 'R-70',
  subject: 'Eau potable (qualité)',
  category: 'Eau potable et égout',
  position: [45.4552, -73.4578]
}, {
  reqId: 'R-103',
  subject: 'Feux de circulation',
  category: 'Éclairage public',
  position: [45.4438, -73.4728]
}, {
  reqId: 'R-57',
  subject: 'Lampadaire défectueux',
  category: 'Éclairage public',
  position: [45.4488, -73.4548]
}];
// Custom indigo map marker
const requestMarkerIcon = L.divIcon({
  className: '',
  html: `<div style="display:flex;align-items:center;justify-content:center;width:28px;height:28px;border-radius:9999px;background:#4F46E5;box-shadow:0 2px 6px rgba(0,0,0,0.3);border:2px solid white;"><svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M4 20h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13c0 1.1.9 2 2 2Z"/></svg></div>`,
  iconSize: [28, 28],
  iconAnchor: [14, 14],
  popupAnchor: [0, -14]
});
export function RequetesGerer() {
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [selectedRequest, setSelectedRequest] = useState<Request | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  if (selectedRequest) {
    return <div className="bg-slate-50 -mx-4 sm:-mx-6 lg:-mx-8 -my-8 min-h-screen flex" data-id="element-5085" data-mp="2m483gvtl79ok">
        {/* Left Pane - Citizen Visible */}
        <div className="w-1/2 border-r border-slate-200 bg-white flex flex-col" data-id="element-5086" data-mp="2r1zvrqy58xta">
          <div className="p-4 border-b border-slate-200 flex items-center gap-2 text-slate-500 text-sm font-medium" data-id="element-5087" data-mp="2v2jpekjidjao">
            <div className="w-4 h-4 rounded-full border-2 border-slate-300 flex items-center justify-center" data-id="element-5088" data-mp="303qxylth6mie">
              <div className="w-1.5 h-1.5 bg-slate-300 rounded-full" data-id="element-5089" data-mp="34zy5gj1is62g"></div>
            </div>
            Visible par le citoyen
          </div>

          <div className="px-6 pt-4 border-b border-slate-200 flex gap-6" data-id="element-5090" data-mp="1an81t9lcnihv">
            <button className="pb-3 border-b-2 border-[#4F46E5] text-[#4F46E5] font-semibold text-sm" data-id="element-5091" data-mp="134idv71pcqbl">
              Infos
            </button>
            <button className="pb-3 border-b-2 border-transparent text-slate-500 hover:text-slate-700 font-semibold text-sm" data-id="element-5092" data-mp="yd09sa4du9me">
              Messages
            </button>
          </div>

          <div className="p-6 overflow-y-auto flex-1" data-id="element-5093" data-mp="ognh9fkkq1t8">
            <div className="flex justify-between items-start mb-8" data-id="element-5094" data-mp="odrjbr8q7d4s">
              <div className="flex gap-4" data-id="element-5095" data-mp="1m2un0ssm6076">
                <div className="w-12 h-12 rounded-xl bg-slate-50 border border-slate-200 flex items-center justify-center flex-shrink-0" data-id="element-5096" data-mp="1h4jc0eql1jnd">
                  <FolderIcon className="w-6 h-6 text-[#4F46E5]" data-id="element-5097" data-mp="1c3vldu69n4to" />
                </div>
                <div data-id="element-5098" data-mp="17527i0n2hbsz">
                  <div className="flex items-center gap-2" data-id="element-5099" data-mp="175kb5ic26sax">
                    <h2 className="text-xl font-bold text-slate-900" data-id="element-5100" data-mp="qjfzeuuye5e">
                      Entretien des parcs
                    </h2>
                    <ChevronLeftIcon className="w-4 h-4 text-slate-400 rotate-[-90deg]" data-id="element-5101" data-mp="3rurmqeromzm7" />
                  </div>
                  <div className="text-sm text-slate-500 mt-1 flex items-center gap-2" data-id="element-5102" data-mp="ldrfo6fglcku">
                    <span className="bg-slate-100 px-2 py-0.5 rounded text-xs font-medium border border-slate-200" data-id="element-5103" data-mp="qfqed3097lbg">
                      R-71
                    </span>
                    <span data-id="element-5104" data-mp="gh0y1eo0a8xb">Parcs et espaces récréatifs</span>
                  </div>
                  <div className="text-sm text-slate-400 mt-1 ml-10 border-l-2 border-slate-200 pl-2" data-id="element-5105" data-mp="14522oci2d57g">
                    Service Des Travaux Publics
                  </div>
                </div>
              </div>
              <div className={`px-3 py-1 rounded-full text-sm font-medium flex items-center gap-2 ${getStatusColor('En Cours')}`} data-id="element-5106" data-mp="vc7584yu8ds8">
                <div className={`w-2 h-2 rounded-full ${getStatusDot('En Cours')}`} data-id="element-5107" data-mp="1n4g1b7qqn44a"></div>
                En Cours
              </div>
            </div>

            <div className="mb-8" data-id="element-5108" data-mp="1rwzdq1dovt7i">
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3" data-id="element-5109" data-mp="3cl1upc6fx6oq">
                DESCRIPTION
              </h3>
              <div className="p-4 rounded-xl border border-slate-200 bg-white text-slate-700 text-sm leading-relaxed" data-id="element-5110" data-mp="37m6yn8jgplmo">
                Le parc près de la rue Principale nécessite un entretien urgent.
                Les poubelles débordent et il y a des branches cassées sur le
                sentier principal suite à la tempête d'hier soir. Cela
                représente un danger pour les piétons et les cyclistes.
              </div>
            </div>

            <div className="grid grid-cols-2 gap-8 mb-8" data-id="element-5111" data-mp="33l5abmr32tzh">
              <div data-id="element-5112" data-mp="33iir2putyq1f">
                <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3" data-id="element-5113" data-mp="2ym23pxvggfpe">
                  DEMANDEUR
                </h3>
                <div className="flex items-start gap-3" data-id="element-5114" data-mp="itblqb5sveyl">
                  <div className="w-8 h-8 rounded-full bg-[#4F46E5] text-white flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5" data-id="element-5115" data-mp="dvtfwtn2xfut">
                    NO
                  </div>
                  <div data-id="element-5116" data-mp="2v6ntztidkxts">
                    <div className="font-semibold text-slate-900 text-sm" data-id="element-5117" data-mp="2v7gxmlm1f77p">
                      Nathalie Ouellet
                    </div>
                    <div className="text-sm text-slate-500 mt-0.5" data-id="element-5118" data-mp="2qdmebjacgu95">
                      (450) 658-1157
                    </div>
                    <div className="text-sm text-slate-500" data-id="element-5119" data-mp="2gh9juj6z6cdc">
                      adjointe.tp@ville.richelieu.qc.ca
                    </div>
                  </div>
                </div>
              </div>
              <div data-id="element-5120" data-mp="27gbhs6g3kexe">
                <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3" data-id="element-5121" data-mp="27hwadqiupnsm">
                  EMPLACEMENT
                </h3>
                <div className="flex items-start gap-2" data-id="element-5122" data-mp="22k3838ko5dxm">
                  <MapPinIcon className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" data-id="element-5123" data-mp="5o5gnacsqzxg" />
                  <div data-id="element-5124" data-mp="rezskt640az">
                    <div className="text-sm text-slate-700" data-id="element-5125" data-mp="qwyh9rnevw7">
                      200 Boulevard Richelieu
                    </div>
                    <div className="text-sm text-slate-700" data-id="element-5126" data-mp="d8zk6s3pgfju">
                      Richelieu, J3L 3R1
                    </div>
                    <button className="text-sm text-[#4F46E5] mt-1 hover:underline" data-id="element-5127" data-mp="hzfw27rvnt43">
                      Voir sur la carte ▾
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div data-id="element-5128" data-mp="3li97adx6o5v3">
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3" data-id="element-5129" data-mp="3lisqdxykl0ap">
                PIÈCES JOINTES
              </h3>
              <div className="flex gap-3" data-id="element-5130" data-mp="3vecie9a0jeu6">
                <div className="w-20 h-20 rounded-lg bg-slate-200 overflow-hidden" data-id="element-5131" data-mp="3vgozkk7y5l66">
                  <img src="https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=200&h=200&fit=crop" alt="Parc" className="w-full h-full object-cover" data-id="element-5132" data-mp="49vz5ikz9q2p" />
                </div>
                <div className="w-20 h-20 rounded-lg bg-slate-200 overflow-hidden" data-id="element-5133" data-mp="3bq339i72gbrm">
                  <img src="https://images.unsplash.com/photo-1519331379826-f10be5486c6f?w=200&h=200&fit=crop" alt="Parc" className="w-full h-full object-cover" data-id="element-5134" data-mp="36rruzbwf69bi" />
                </div>
                <button className="w-20 h-20 rounded-lg border-2 border-dashed border-slate-300 flex flex-col items-center justify-center text-slate-400 hover:text-[#4F46E5] hover:border-[#4F46E5] transition-colors" data-id="element-5135" data-mp="3gqh7ep5jr1kd">
                  <PlusIcon className="w-5 h-5 mb-1" data-id="element-5136" data-mp="2nz7768vkuuek" />
                  <span className="text-xs font-medium" data-id="element-5137" data-mp="31t8alfxh55l9">Téléverser</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Right Pane - Internal */}
        <div className="w-1/2 bg-slate-50 flex flex-col relative" data-id="element-5138" data-mp="29at5405zj9z2">
          <button onClick={() => setSelectedRequest(null)} className="absolute top-4 right-4 p-2 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-200 transition-colors z-10" data-id="element-5139" data-mp="25b283epv7xm3">
            <XIcon className="w-5 h-5" data-id="element-5140" data-mp="1zydjky3d8q9h" />
          </button>

          <div className="p-4 border-b border-slate-200 flex items-center justify-between text-slate-500 text-sm font-medium bg-white" data-id="element-5141" data-mp="1l8fqc3qkxadf">
            <div className="flex items-center gap-2" data-id="element-5142" data-mp="1q8kac3qp4n8e">
              <div className="w-4 h-4 rounded flex items-center justify-center border-2 border-slate-300" data-id="element-5143" data-mp="2dx3jelhkl41j">
                <div className="w-2 h-2 bg-slate-300 rounded-sm" data-id="element-5144" data-mp="2ivwn5m721goi"></div>
              </div>
              Interne — non visible par le citoyen
            </div>
            <PrinterIcon className="w-4 h-4 mr-10" data-id="element-5145" data-mp="23ynr4f6t3oqg" />
          </div>

          <div className="px-6 pt-4 border-b border-slate-200 flex gap-6 bg-white" data-id="element-5146" data-mp="28wz0ypx3pz8o">
            <button className="pb-3 border-b-2 border-[#4F46E5] text-[#4F46E5] font-semibold text-sm" data-id="element-5147" data-mp="sj8vbeqc32wx">
              Gestion
            </button>
            <button className="pb-3 border-b-2 border-transparent text-slate-500 hover:text-slate-700 font-semibold text-sm" data-id="element-5148" data-mp="xk4zzuiiet3e">
              Collaboration
            </button>
            <button className="pb-3 border-b-2 border-transparent text-slate-500 hover:text-slate-700 font-semibold text-sm" data-id="element-5149" data-mp="18yhps470rww3">
              Historique
            </button>
          </div>

          <div className="p-6 overflow-y-auto flex-1 space-y-6" data-id="element-5150" data-mp="z1v8tqhtdpzc">
            <div className="text-sm text-slate-500 font-medium" data-id="element-5151" data-mp="1wou4bxwrsrvn">
              19 mai <span className="mx-2 text-slate-300" data-id="element-5152" data-mp="1wmrizr6kbmx9">→</span>{' '}
              <span className="text-red-500" data-id="element-5153" data-mp="1rqsp5o2gtcpg">26 mai</span>
            </div>

            {/* Tasks */}
            <div className="space-y-3" data-id="element-5154" data-mp="1mpmql9m4iv80">
              <div className="flex items-start gap-3" data-id="element-5155" data-mp="1mshexc89d06p">
                <CheckSquareIcon className="w-5 h-5 text-slate-900 mt-0.5" data-id="element-5156" data-mp="1hqjqm3l03f6w" />
                <div className="flex-1" data-id="element-5157" data-mp="bc0wrb5bk5g5">
                  <div className="text-sm text-slate-500 line-through" data-id="element-5158" data-mp="bd2f80onzu7w">
                    Vérifier la disponibilité des équipes
                  </div>
                  <div className="text-xs text-slate-400 mt-0.5" data-id="element-5159" data-mp="2i09v1gk613lq">
                    Terminée: 22 mai 2026{' '}
                    <PencilIcon className="w-3 h-3 inline ml-1" data-id="element-5160" data-mp="2my2yi1u33jib" />
                  </div>
                </div>
                <div className="text-xs text-red-500 flex items-center gap-1" data-id="element-5161" data-mp="2rx5vcyzwujf7">
                  <CalendarIcon className="w-3 h-3" data-id="element-5162" data-mp="2woxonhagb391" /> 22 mai 2026
                </div>
                <div className="w-6 h-6 rounded-full bg-indigo-500 text-white flex items-center justify-center text-[10px] font-bold" data-id="element-5163" data-mp="31o0rcv2ykxdu">
                  ML
                </div>
              </div>
              <div className="flex items-start gap-3" data-id="element-5164" data-mp="3aooz581re1ia">
                <SquareIcon className="w-5 h-5 text-slate-300 mt-0.5" data-id="element-5165" data-mp="3ap8eqi3xsdt5" />
                <div className="flex-1" data-id="element-5166" data-mp="3fmh0sts7h1zo">
                  <div className="text-sm text-slate-700" data-id="element-5167" data-mp="3fl5x0tyrfu8k">
                    Planifier le ramassage des branches
                  </div>
                </div>
                <div className="text-xs text-red-500 flex items-center gap-1" data-id="element-5168" data-mp="1laad4vi4ksqh">
                  <CalendarIcon className="w-3 h-3" data-id="element-5169" data-mp="1d81duynvzvue" /> 25 mai 2026
                </div>
                <div className="w-6 h-6 rounded-full bg-[#4F46E5] text-white flex items-center justify-center text-[10px] font-bold" data-id="element-5170" data-mp="197pudp79g1ki">
                  ST
                </div>
              </div>
              <div className="flex items-start gap-3" data-id="element-5171" data-mp="1i85ucoosoqkm">
                <SquareIcon className="w-5 h-5 text-slate-300 mt-0.5" data-id="element-5172" data-mp="1i6l255pydjpx" />
                <div className="flex-1" data-id="element-5173" data-mp="1w6m30l67wr5e">
                  <div className="text-sm text-slate-700" data-id="element-5174" data-mp="1w5axogx4jbc6">
                    Contacter le demandeur pour suivi
                  </div>
                </div>
                <div className="text-xs text-red-500 flex items-center gap-1" data-id="element-5175" data-mp="25wfezna9v8a6">
                  <CalendarIcon className="w-3 h-3" data-id="element-5176" data-mp="20y42f4slejo9" /> 20 mai 2026
                </div>
                <div className="w-6 h-6 rounded-full border border-dashed border-slate-300 text-slate-400 flex items-center justify-center text-[10px]" data-id="element-5177" data-mp="2fvekiwzqqnsy">
                  <UserIcon className="w-3 h-3" data-id="element-5178" data-mp="2ayehkuy6u162" />
                </div>
              </div>
              <button className="text-sm text-slate-500 font-medium flex items-center gap-1 hover:text-slate-700 mt-2" data-id="element-5179" data-mp="2uiv3xce89a9s">
                <PlusIcon className="w-4 h-4" data-id="element-5180" data-mp="2yhuh1ka44dcb" /> Ajouter une tâche
              </button>
            </div>

            {/* Linked Requests */}
            <div className="bg-white rounded-xl border border-slate-200 p-5" data-id="element-5181" data-mp="2piqw0ardxnv6">
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4" data-id="element-5182" data-mp="2audy388qluy1">
                REQUÊTES LIÉES
              </h3>
              <div className="space-y-3" data-id="element-5183" data-mp="2flw0m0qes3l4">
                <div className="flex items-center justify-between p-2 hover:bg-slate-50 rounded-lg border border-transparent hover:border-slate-100 transition-colors" data-id="element-5184" data-mp="2fmxaw2zlom1s">
                  <div className="flex items-center gap-3" data-id="element-5185" data-mp="26taoh3ha8v59">
                    <span className="text-xs font-medium text-slate-500 bg-slate-100 px-2 py-1 rounded border border-slate-200" data-id="element-5186" data-mp="1rv8fueve9jh5">
                      Liée ▾
                    </span>
                    <span className="text-xs font-medium text-slate-500 bg-slate-100 px-2 py-1 rounded border border-slate-200" data-id="element-5187" data-mp="2cccld0jtc57a">
                      R-68 ↗
                    </span>
                    <span className="text-sm text-slate-700" data-id="element-5188" data-mp="23bxzo02xso39">
                      Lampadaire défectueux
                    </span>
                  </div>
                  <div className={`px-2 py-0.5 rounded-full text-xs font-medium flex items-center gap-1.5 ${getStatusColor('En Attente')}`} data-id="element-5189" data-mp="1yc1wu45z72pa">
                    <div className={`w-1.5 h-1.5 rounded-full ${getStatusDot('En Attente')}`} data-id="element-5190" data-mp="1olz4852rnqrh"></div>
                    En Attente
                  </div>
                </div>
                <div className="flex items-center justify-between p-2 hover:bg-slate-50 rounded-lg border border-transparent hover:border-slate-100 transition-colors" data-id="element-5191" data-mp="1fnwvt4gpdvu6">
                  <div className="flex items-center gap-3" data-id="element-5192" data-mp="15nw8l7fpqvkp">
                    <span className="text-xs font-medium text-slate-500 bg-slate-100 px-2 py-1 rounded border border-slate-200" data-id="element-5193" data-mp="15qig572ltb2x">
                      Bloque ▾
                    </span>
                    <span className="text-xs font-medium text-slate-500 bg-slate-100 px-2 py-1 rounded border border-slate-200" data-id="element-5194" data-mp="10leytvpcyphj">
                      R-72 ↗
                    </span>
                    <span className="text-sm text-slate-700" data-id="element-5195" data-mp="1agzwjhne93lh">
                      Nid-de-poule rue Principale
                    </span>
                  </div>
                  <div className={`px-2 py-0.5 rounded-full text-xs font-medium flex items-center gap-1.5 ${getStatusColor('Reçue')}`} data-id="element-5196" data-mp="hsayjrhmxedy">
                    <div className={`w-1.5 h-1.5 rounded-full ${getStatusDot('Reçue')}`} data-id="element-5197" data-mp="rinkno53lfm7"></div>
                    Reçue
                  </div>
                </div>
                <div className="flex items-center justify-between p-2 hover:bg-slate-50 rounded-lg border border-transparent hover:border-slate-100 transition-colors" data-id="element-5198" data-mp="3v7h6l4ds8l3x">
                  <div className="flex items-center gap-3" data-id="element-5199" data-mp="1rcs1hge5hwk3">
                    <span className="text-xs font-medium text-slate-500 bg-slate-100 px-2 py-1 rounded border border-slate-200" data-id="element-5200" data-mp="1w8plbwomhl1m">
                      Doublon ▾
                    </span>
                    <span className="text-xs font-medium text-slate-500 bg-slate-100 px-2 py-1 rounded border border-slate-200" data-id="element-5201" data-mp="25aqg8mwdvv04">
                      R-75 ↗
                    </span>
                    <span className="text-sm text-slate-700" data-id="element-5202" data-mp="1dirbpc56fjvv">
                      Banc de parc endommagé
                    </span>
                  </div>
                  <div className={`px-2 py-0.5 rounded-full text-xs font-medium flex items-center gap-1.5 ${getStatusColor('En Cours')}`} data-id="element-5203" data-mp="1hirpqvod5t2e">
                    <div className={`w-1.5 h-1.5 rounded-full ${getStatusDot('En Cours')}`} data-id="element-5204" data-mp="pt6scs542km2"></div>
                    En Cours
                  </div>
                </div>
              </div>
              <button className="text-sm text-slate-500 font-medium flex items-center gap-1 hover:text-slate-700 mt-3" data-id="element-5205" data-mp="m9ennq47jf14">
                <PlusIcon className="w-4 h-4" data-id="element-5206" data-mp="daucrb4w29ng" /> Lier une requête
              </button>
            </div>

            {/* Tags */}
            <div className="bg-white rounded-xl border border-slate-200 p-5" data-id="element-5207" data-mp="8ay9xf7xgo9h">
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4" data-id="element-5208" data-mp="3dg9yebw1jdm">
                ÉTIQUETTES
              </h3>
              <div className="flex flex-wrap gap-2" data-id="element-5209" data-mp="3uq116e7p41n4">
                <span className="px-3 py-1 rounded-full bg-indigo-50 text-indigo-600 text-xs font-medium border border-indigo-100 flex items-center gap-1" data-id="element-5210" data-mp="3przku11nmgfc">
                  Urgent <XIcon className="w-3 h-3 cursor-pointer" data-id="element-5211" data-mp="3lot9iezopz20" />
                </span>
                <span className="px-3 py-1 rounded-full bg-indigo-50 text-indigo-600 text-xs font-medium border border-indigo-100 flex items-center gap-1" data-id="element-5212" data-mp="3gr1f9ebki0un">
                  Suivi requis <XIcon className="w-3 h-3 cursor-pointer" data-id="element-5213" data-mp="3242ozmog5chi" />
                </span>
                <button className="px-3 py-1 rounded-full border border-dashed border-slate-300 text-slate-500 text-xs font-medium flex items-center gap-1 hover:border-slate-400 hover:text-slate-700 transition-colors" data-id="element-5214" data-mp="374qicf05e5ew">
                  <PlusIcon className="w-3 h-3" data-id="element-5215" data-mp="2x6ssjn2n40k6" /> Ajouter une étiquette
                </button>
              </div>
            </div>

            {/* Internal Files */}
            <div className="bg-white rounded-xl border border-slate-200 p-5" data-id="element-5216" data-mp="3puf4pjpxodin">
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4" data-id="element-5217" data-mp="3pwjcicqydwo7">
                FICHIERS INTERNES
              </h3>
              <div className="flex gap-3" data-id="element-5218" data-mp="3g3u1fizbekte">
                <div className="w-20 rounded-lg border border-slate-200 p-2 flex flex-col items-center justify-center text-center" data-id="element-5219" data-mp="3g2ivbcied0z4">
                  <FileTextIcon className="w-8 h-8 text-red-500 mb-2" data-id="element-5220" data-mp="cgbwx5d00ll9" />
                  <div className="text-[10px] font-medium text-slate-700 truncate w-full" data-id="element-5221" data-mp="chmv8pnod14z">
                    rapport_inspe...
                  </div>
                  <div className="text-[9px] text-slate-400" data-id="element-5222" data-mp="1s6bcnl4jo7pn">22 mai</div>
                </div>
                <div className="w-20 h-24 rounded-lg bg-slate-200 overflow-hidden" data-id="element-5223" data-mp="26uz5hjn0a3bw">
                  <img src="https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=200&h=200&fit=crop" alt="Doc" className="w-full h-full object-cover" data-id="element-5224" data-mp="21wnu34h2bkrk" />
                </div>
                <div className="w-20 h-24 rounded-lg bg-slate-200 overflow-hidden" data-id="element-5225" data-mp="19alb1z0ixdca">
                  <img src="https://images.unsplash.com/photo-1518495973542-4542c06a5843?w=200&h=200&fit=crop" alt="Doc" className="w-full h-full object-cover" data-id="element-5226" data-mp="1n5x31ynaoppi" />
                </div>
                <button className="w-20 h-24 rounded-lg border-2 border-dashed border-slate-300 flex flex-col items-center justify-center text-slate-400 hover:text-[#4F46E5] hover:border-[#4F46E5] transition-colors" data-id="element-5227" data-mp="2ukjqe82pzant">
                  <PlusIcon className="w-5 h-5 mb-1" data-id="element-5228" data-mp="3txvb0foir8ev" />
                  <span className="text-xs font-medium" data-id="element-5229" data-mp="2rbucjw04o8n">Téléverser</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>;
  }
  return <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden" data-id="element-5230" data-mp="cphryparz87p">
      {/* Header */}
      <div className="p-4 border-b border-slate-200" data-id="element-5231" data-mp="gqbdxd4nqcxy">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4" data-id="element-5232" data-mp="goqkjqu2x01o">
          <div className="flex items-center gap-3" data-id="element-5233" data-mp="libk3nbbm22z">
            <div className="inline-flex items-center bg-slate-100 rounded-lg p-1 gap-1" data-id="element-5234" data-mp="qgwbcx8lhbfa">
              <button onClick={() => setViewMode('list')} className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-semibold transition-colors ${viewMode === 'list' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-5235" data-mp="vg99wf8tw6xp">
                <TableIcon className="w-4 h-4" data-id="element-5236" data-mp="2fxifnwpu4yei" />
                Tableau
              </button>
              <button onClick={() => setViewMode('map')} className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-semibold transition-colors ${viewMode === 'map' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-5237" data-mp="2ay5iai1c88xo">
                <MapIcon className="w-4 h-4" data-id="element-5238" data-mp="2ynzuqyhqvvhe" />
                Carte
              </button>
            </div>
            <span className="text-sm text-slate-500" data-id="element-5239" data-mp="2twhtsafq1uwf">10 demandes</span>
          </div>

          <div className="flex items-center gap-3" data-id="element-5240" data-mp="2oz87h3nmpzct">
            <div className="relative" data-id="element-5241" data-mp="2owdlh7oyw6ha">
              <SearchIcon className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" data-id="element-5242" data-mp="3medqayzjgcgq" />
              <input type="text" placeholder="Rechercher des requêtes..." className="pl-9 pr-4 py-2 border border-slate-200 rounded-lg text-sm w-64 focus:outline-none focus:ring-2 focus:ring-[#4F46E5] focus:border-transparent" data-id="element-5243" data-mp="3hmvm8227xvrj" />
            </div>
            <button className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 text-slate-700 text-sm font-semibold rounded-lg hover:bg-slate-50 transition-colors" data-id="element-5244" data-mp="15yqswmwnkgij">
              <FilterIcon className="w-4 h-4" data-id="element-5245" data-mp="wykij9w93wnl" />
              Filtrer
            </button>
            <button onClick={() => setShowCreate(true)} className="flex items-center gap-2 px-4 py-2 bg-slate-900 text-white text-sm font-semibold rounded-lg hover:bg-slate-800 transition-colors whitespace-nowrap" data-id="element-5246" data-mp="110ffk275fnvk">
              <PlusIcon className="w-4 h-4" data-id="element-5247" data-mp="s098ay21nk4u" />
              Créer une requête
            </button>
          </div>
        </div>
      </div>

      {/* Map View */}
      {viewMode === 'map' && <div className="h-[400px] w-full border-b border-slate-200" data-id="element-5248" data-mp="2md8vbc8r7gm5">
          <MapContainer center={[45.4499, -73.4653]} zoom={13} scrollWheelZoom={false} style={{
        height: '100%',
        width: '100%'
      }} data-id="element-5249" data-mp="arq7zsafk8t9">
            <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" data-id="element-5250" data-mp="khsz1mxzrcoy" />
            {MAP_MARKERS.map(m => <Marker key={m.reqId} position={m.position} icon={requestMarkerIcon} data-id="element-5251" data-mp="oil8ag6v5hl8">
                <Popup data-id="element-5252" data-mp="13eko6d07sm0x">
                  <div className="text-sm" data-id="element-5253" data-mp="yg9fw6pkijkt">
                    <div className="font-semibold text-slate-900" data-id="element-5254" data-mp="yhasifm8fe4n">
                      {m.reqId} — {m.subject}
                    </div>
                    <div className="text-slate-500" data-id="element-5255" data-mp="358tghwgem076">{m.category}</div>
                  </div>
                </Popup>
              </Marker>)}
          </MapContainer>
        </div>}

      {/* Table */}
      <div className="overflow-x-auto" data-id="element-5256" data-mp="2jdjniqp0gl14">
        <table className="w-full text-left border-collapse" data-id="element-5257" data-mp="2jci33vm3um6q">
          <thead data-id="element-5258" data-mp="2o4i7q1d4e39n">
            <tr className="border-b border-slate-200 bg-white" data-id="element-5259" data-mp="2o402if8hcepl">
              <th className="px-4 py-3 text-xs font-semibold text-slate-500 whitespace-nowrap" data-id="element-5260" data-mp="2o6mbmjb0r29x">
                <span className="inline-flex items-center gap-1" data-id="element-5261" data-mp="2t55v8d2541z4">
                  Sujet{' '}
                  <ChevronsUpDownIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-5262" data-mp="1vhxgr20k3t6m" />
                </span>
              </th>
              <th className="px-4 py-3 text-xs font-semibold text-slate-500 whitespace-nowrap" data-id="element-5263" data-mp="25e0hc3frfjrg">
                <span className="inline-flex items-center gap-1" data-id="element-5264" data-mp="29fdl87zalcvq">
                  Catégorie{' '}
                  <ChevronsUpDownIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-5265" data-mp="3gt7ccaddwqq5" />
                </span>
              </th>
              <th className="px-4 py-3 text-xs font-semibold text-slate-500 whitespace-nowrap" data-id="element-5266" data-mp="3ktr8pbq4vqb8">
                <span className="inline-flex items-center gap-1" data-id="element-5267" data-mp="197p50fht626f">
                  ID Requête{' '}
                  <ChevronsUpDownIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-5268" data-mp="155bwxkfl9s6h" />
                </span>
              </th>
              <th className="px-4 py-3 text-xs font-semibold text-slate-500 whitespace-nowrap" data-id="element-5269" data-mp="10dkgi2q5d2u2">
                Emplacement
              </th>
              <th className="px-4 py-3 text-xs font-semibold text-slate-500 whitespace-nowrap" data-id="element-5270" data-mp="vdy1pouo7ej5">
                <span className="inline-flex items-center gap-1" data-id="element-5271" data-mp="qebecml4ohwm">
                  Date de création{' '}
                  <ChevronsUpDownIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-5272" data-mp="hfzqgsbnby7v" />
                </span>
              </th>
              <th className="px-4 py-3 text-xs font-semibold text-slate-500 whitespace-nowrap" data-id="element-5273" data-mp="chg2ynhemede">
                <span className="inline-flex items-center gap-1" data-id="element-5274" data-mp="2btqzpqq2cg2s">
                  Statut{' '}
                  <ChevronsUpDownIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-5275" data-mp="26v7bfjnzys79" />
                </span>
              </th>
              <th className="px-4 py-3 text-xs font-semibold text-slate-500 whitespace-nowrap" data-id="element-5276" data-mp="2x41q1wb3tj52">
                <span className="inline-flex items-center gap-1" data-id="element-5277" data-mp="2s6jq2vf2ee97">
                  Département{' '}
                  <ChevronsUpDownIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-5278" data-mp="35xet3r2vu14v" />
                </span>
              </th>
              <th className="px-4 py-3 text-xs font-semibold text-slate-500 whitespace-nowrap" data-id="element-5279" data-mp="31wuuej2nupgm">
                Demandeur
              </th>
              <th className="px-4 py-3 text-xs font-semibold text-slate-500 whitespace-nowrap" data-id="element-5280" data-mp="2e7trtcgie8j9">
                Date SLA
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-id="element-5281" data-mp="2n7ypuvztbukr">
            {MOCK_REQUESTS.map(req => <tr key={req.id} onClick={() => setSelectedRequest(req)} className="hover:bg-slate-50 cursor-pointer transition-colors" data-id="element-5282" data-mp="2i69e1e4rncvm">
                <td className="px-4 py-3 text-sm text-slate-700 whitespace-nowrap" data-id="element-5283" data-mp="3tmhkhqnbj8ck">
                  <div className="flex items-center gap-2" data-id="element-5284" data-mp="5wsq2rslv0yb">
                    <div className="w-8 h-8 rounded bg-slate-50 border border-slate-200 flex items-center justify-center" data-id="element-5285" data-mp="a0gyfcp2a0km">
                      <FolderIcon className="w-4 h-4 text-[#4F46E5]" data-id="element-5286" data-mp="3s6ls84oyuxl8" />
                    </div>
                    {req.subject}
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap" data-id="element-5287" data-mp="oobsawet2aza">
                  {req.category}
                </td>
                <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap" data-id="element-5288" data-mp="tnexqhyp6j7s">
                  {req.reqId}
                </td>
                <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap" data-id="element-5289" data-mp="ex7monb0s2h4">
                  <div className="flex items-center gap-1" data-id="element-5290" data-mp="jw269rgt9v2a">
                    {req.location}
                    <MapPinIcon className="w-3 h-3 text-[#4F46E5]" data-id="element-5291" data-mp="17lmq0cksgwcm" />
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap" data-id="element-5292" data-mp="1cg94iom6350x">
                  {req.date}
                </td>
                <td className="px-4 py-3 whitespace-nowrap" data-id="element-5293" data-mp="1hatl91byxihc">
                  <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(req.status)}`} data-id="element-5294" data-mp="1h9ikladtkqug">
                    <div className={`w-1.5 h-1.5 rounded-full ${getStatusDot(req.status)}`} data-id="element-5295" data-mp="18adk3yip6nfn"></div>
                    {req.status}
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap" data-id="element-5296" data-mp="yj0u7cggm7c9">
                  {req.department}
                </td>
                <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap" data-id="element-5297" data-mp="onfp35g3gqya">
                  <div className="flex items-center gap-2" data-id="element-5298" data-mp="klkns0y5ydki">
                    <div className={`w-6 h-6 rounded-full ${req.requester.color} text-white flex items-center justify-center text-[10px] font-bold`} data-id="element-5299" data-mp="kn5gdl0x3mfq">
                      {req.requester.initials}
                    </div>
                    {req.requester.name}
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap" data-id="element-5300" data-mp="309rm9z8ob4dj">
                  {req.slaDate}
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="p-4 border-t border-slate-200 flex items-center justify-end gap-6 text-sm text-slate-500" data-id="element-5301" data-mp="28koewalhpdwo">
        <div className="flex items-center gap-2" data-id="element-5302" data-mp="2ci2nc01cjhnm">
          Éléments par page
          <select className="border-b border-slate-300 pb-1 focus:outline-none bg-transparent font-medium text-slate-700" data-id="element-5303" data-mp="2hi75rvltemgh">
            <option data-id="element-5304" data-mp="2mc326tmfk19j">10</option>
            <option data-id="element-5305" data-mp="1ove2p6kydb3b">20</option>
            <option data-id="element-5306" data-mp="1ou2umukh5h6e">50</option>
          </select>
        </div>
        <div data-id="element-5307" data-mp="1ttfj1jqzuwb3">1 - 10 / 98</div>
        <div className="flex items-center gap-4" data-id="element-5308" data-mp="1tsml98a0jh53">
          <button className="hover:text-slate-900" data-id="element-5309" data-mp="b883gntbra4">
            <ChevronLeftIcon className="w-4 h-4" data-id="element-5310" data-mp="c00yjq0zawp" />
          </button>
          <button className="hover:text-slate-900" data-id="element-5311" data-mp="3ri1gb4h0wqo5">
            <ChevronRightIcon className="w-4 h-4" data-id="element-5312" data-mp="3rff0yinw56vd" />
          </button>
        </div>
      </div>

      {showCreate && <CreateRequeteDialog onClose={() => setShowCreate(false)} data-id="element-5313" data-mp="4ctt5pai3bve" />}
    </div>;
}
// Missing icons for the component
function CalendarIcon(props: React.SVGProps<SVGSVGElement>) {
  return <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-5314" data-mp="e2lq2q62oh31">
      <rect width="18" height="18" x="3" y="4" rx="2" ry="2" data-id="element-5315" data-mp="o1auph8yk51c" />
      <line x1="16" x2="16" y1="2" y2="6" data-id="element-5316" data-mp="2tt7z3giz1smp" />
      <line x1="8" x2="8" y1="2" y2="6" data-id="element-5317" data-mp="2tu17sn1hqs7h" />
      <line x1="3" x2="21" y1="10" y2="10" data-id="element-5318" data-mp="2psfmqeesz7m7" />
    </svg>;
}
function UserIcon(props: React.SVGProps<SVGSVGElement>) {
  return <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-5319" data-mp="2fpabfn2lf2ak">
      <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" data-id="element-5320" data-mp="2axit1ztlc2vi" />
      <circle cx="12" cy="7" r="4" data-id="element-5321" data-mp="2avxw63jt07uh" />
    </svg>;
}
function PencilIcon(props: React.SVGProps<SVGSVGElement>) {
  return <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-5322" data-mp="1w2l1h969nhfh">
      <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" data-id="element-5323" data-mp="1rzfyl5i9xdqb" />
      <path d="m15 5 4 4" data-id="element-5324" data-mp="3hd1ln396x1aj" />
    </svg>;
}